/**
 * Flight Search Date Utilities
 * 
 * CRITICAL: Flight journey dates are CALENDAR dates, not timestamps.
 * They must remain timezone-agnostic throughout the search flow.
 * 
 * FORBIDDEN PATTERNS (cause N-1 bug for western timezones):
 * - new Date("YYYY-MM-DD")      → parses as UTC midnight
 * - date.toISOString().split()  → converts to UTC before extracting
 * 
 * These utilities ensure user-selected dates remain unchanged regardless
 * of browser or server timezone.
 */

const YMD_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Validate if value is a YYYY-MM-DD string.
 * @param {any} value
 * @returns {boolean}
 */
export function isValidYmd(value) {
  return typeof value === 'string' && YMD_REGEX.test(value);
}

/**
 * Convert Date object to YYYY-MM-DD using LOCAL calendar parts.
 * Safe for date picker outputs.
 * 
 * @param {Date} date
 * @returns {string} YYYY-MM-DD or empty string if invalid
 */
export function dateObjectToYmd(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert YYYY-MM-DD string to local Date object for UI components.
 * Uses new Date(year, month-1, day) to avoid UTC interpretation.
 * 
 * @param {string} ymd - YYYY-MM-DD format or ISO datetime
 * @returns {Date|null}
 */
export function ymdToLocalDate(ymd) {
  if (!ymd) return null;
  
  const str = String(ymd);
  
  // Match YYYY-MM-DD exactly
  const match = str.match(YMD_REGEX);
  if (match) {
    return new Date(
      parseInt(match[1], 10),
      parseInt(match[2], 10) - 1,
      parseInt(match[3], 10)
    );
  }
  
  // Try extracting from ISO datetime (e.g., "2026-06-05T14:30:00")
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoMatch) {
    return new Date(
      parseInt(isoMatch[1], 10),
      parseInt(isoMatch[2], 10) - 1,
      parseInt(isoMatch[3], 10)
    );
  }
  
  return null;
}

/**
 * Normalize any date input to YYYY-MM-DD string.
 * - If already YYYY-MM-DD, returns unchanged.
 * - If Date object, extracts local parts.
 * - If ISO datetime, extracts date portion via regex (no parsing).
 * 
 * @param {string|Date|null|undefined} value
 * @returns {string} YYYY-MM-DD or empty string
 */
export function normalizeYmd(value) {
  if (!value) return '';
  
  // Already valid YYYY-MM-DD
  if (isValidYmd(value)) return value;
  
  // Date object → extract local parts
  if (value instanceof Date) {
    return dateObjectToYmd(value);
  }
  
  // String: try to extract date portion via regex
  const str = String(value);
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  
  return '';
}

/**
 * Add/subtract days from a YYYY-MM-DD string.
 * Returns result as YYYY-MM-DD string.
 * 
 * @param {string} ymd - YYYY-MM-DD format
 * @param {number} days - positive or negative
 * @returns {string} YYYY-MM-DD or empty string if invalid input
 */
export function addDaysToYmd(ymd, days) {
  const date = ymdToLocalDate(ymd);
  if (!date) return '';
  date.setDate(date.getDate() + days);
  return dateObjectToYmd(date);
}

/**
 * Get today's date as YYYY-MM-DD in local timezone.
 * @returns {string} YYYY-MM-DD
 */
export function todayYmd() {
  return dateObjectToYmd(new Date());
}

/**
 * Compare two YYYY-MM-DD strings lexicographically.
 * @param {string|Date} ymd1
 * @param {string|Date} ymd2
 * @returns {number|null} -1 if ymd1 < ymd2, 0 if equal, 1 if ymd1 > ymd2, null if invalid
 */
export function compareYmd(ymd1, ymd2) {
  const a = normalizeYmd(ymd1);
  const b = normalizeYmd(ymd2);
  if (!a || !b) return null;
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Add months to YYYY-MM-DD string with proper month-end clamping.
 * Handles cases like:
 * - 2026-08-31 + 6 months = 2027-02-28 (Feb has 28 days)
 * - 2026-01-31 + 1 month = 2026-02-28 (Feb has 28 days)
 * - 2024-01-31 + 1 month = 2024-02-29 (leap year Feb has 29 days)
 * 
 * @param {string} ymd - YYYY-MM-DD format
 * @param {number} months - positive or negative
 * @returns {string} YYYY-MM-DD or empty string if invalid input
 */
export function addMonthsToYmd(ymd, months) {
  const normalized = normalizeYmd(ymd);
  if (!normalized) return '';
  
  const [yearStr, monthStr, dayStr] = normalized.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1;
  const day = parseInt(dayStr, 10);
  
  const targetMonth = month + months;
  const targetYear = year + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  
  const daysInTargetMonth = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const clampedDay = Math.min(day, daysInTargetMonth);
  
  return dateObjectToYmd(new Date(targetYear, normalizedMonth, clampedDay));
}
